import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { orpc } from '../../apis/orpc-query';
import { downloadSimulatorExport } from './export-download';
import './simulator.css';

const FONTS_ID = 'simx-fonts';
const FONTS_HREF =
  'https://fonts.googleapis.com/css2?family=IBM+Plex+Mono:wght@400;500&family=IBM+Plex+Sans:wght@400;500;600&family=Source+Serif+4:opsz,wght@8..60,600&display=swap';

/**
 * The public simulator's frame: a navigation rail on the left — Simulator, Saved estimates, Export to
 * Excel — and the working area on the right. No sign-in, no app shell — this is the testing tool the
 * pricing team opens from a link, laid out like a small system rather than a report.
 */
export const SimulatorShell = () => {
  const list = useQuery(orpc.simulator.list.queryOptions());
  const location = useLocation();
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  useEffect(() => {
    const previousTitle = document.title;

    document.title = 'Depreciation Simulator';

    if (!document.getElementById(FONTS_ID)) {
      const link = document.createElement('link');

      link.id = FONTS_ID;
      link.rel = 'stylesheet';
      link.href = FONTS_HREF;
      document.head.appendChild(link);
    }

    return () => {
      document.title = previousTitle;
    };
  }, []);

  const onExport = async (): Promise<void> => {
    setExporting(true);
    setExportError(null);

    try {
      await downloadSimulatorExport();
    } catch (error) {
      setExportError(error instanceof Error ? error.message : 'The export could not be built.');
    } finally {
      setExporting(false);
    }
  };

  const count = list.data?.items.length;
  const onSaved = location.pathname.startsWith('/simulator/estimates');

  return (
    <div className="simx shell">
      <aside className="rail">
        <div className="brandmark">
          <span className="eyebrow">Preowned pricing</span>
          <b>Depreciation simulator</b>
        </div>
        <nav aria-label="Simulator pages">
          <NavLink
            to="/simulator"
            end
            className={({ isActive }) => `item${isActive ? ' active' : ''}`}
          >
            Simulator
          </NavLink>
          <NavLink
            to="/simulator/estimates"
            className={({ isActive }) => `item${isActive ? ' active' : ''}`}
          >
            Saved estimates
            {count !== undefined && <span className="count">{count}</span>}
          </NavLink>
          <button
            type="button"
            className="item"
            disabled={exporting || count === 0}
            title={count === 0 ? 'Save an estimate first' : 'Download every saved estimate as the review sheet'}
            onClick={() => void onExport()}
          >
            {exporting ? 'Building the sheet…' : 'Export to Excel'}
          </button>
        </nav>
        {exportError && <span className="rail-note notice err">{exportError}</span>}
        <div className="rail-foot">
          Prices in JOD, rounded to 100 like the tool. The ladders, factors and values are proposals for discussion; everything is editable in the
          values panel and every edit is recorded on the saved estimate.
        </div>
      </aside>
      <main className="content">
        <header className="masthead">
          <div className="eyebrow">{onSaved ? 'Saved estimates' : 'Simulate a vehicle'}</div>
          <h1>{onSaved ? 'Saved estimates' : 'Depreciation simulator'}</h1>
          <p className="lede">
            {onSaved
              ? 'Every evaluation saved from the simulator, newest first. The export lists them oldest first in the review template’s columns.'
              : 'Choose a vehicle, say how fast it depreciates, describe the car and what the inspection found, and see where its value sits between the best and the worst case over seven years. Save each evaluation for the review sheet.'}
          </p>
        </header>
        <Outlet />
        <footer>
          Prepared for the Preowned pricing discussion · October 2026 · The pricing tool’s current template uses Normal 15 / 10 / 7 / 5 and Luxury 20
          / 15 / 10 / 5 and none of the brand, powertrain, usage, specs, warranty, agency, service or market factors.
        </footer>
      </main>
    </div>
  );
};
